const PHONE_LENGTH_GROUPS = Object.freeze({
  4: ["NU", "SH", "TK"],
  5: ["CK", "FK"],
  6: ["AD", "AQ", "FO", "GL", "NC", "NF", "PM", "PN", "TV", "WF"],
  7: [
    "AG", "AI", "AN", "AS", "AW", "BM", "BN", "BS", "CV", "DM", "ER",
    "FJ", "FM", "GD", "GM", "GU", "GY", "IO", "IS", "KM", "KN", "KY",
    "LC", "LI", "MH", "MP", "MS", "MV", "NR", "PW", "SB", "SC", "SR",
    "ST", "TC", "TO", "TT", "VC", "VG", "VU", "WS"
  ],
  8: [
    "AM", "BH", "BI", "BO", "BT", "BW", "CF", "CR", "CU", "CY", "DJ",
    "DK", "EE", "GA", "GI", "GT", "HK", "HN", "HT", "KI", "KW", "LB",
    "LS", "LT", "LV", "MD", "MK", "ML", "MN", "MO", "MR", "MT", "MU",
    "NE", "NI", "NO", "OM", "PA", "PF", "PG", "QA", "SG", "SI", "SJ",
    "SL", "SV", "SZ", "TD", "TG", "TM", "TN", "TP", "UY"
  ],
  9: [
    "AE", "AF", "AL", "AO", "AZ", "BE", "BG", "CC", "CD", "CG", "CH",
    "CL", "CM", "CX", "CZ", "DZ", "EC", "EH", "ES", "ET", "FR", "GE",
    "GF", "GH", "GN", "GP", "GQ", "GW", "HR", "HU", "IE", "IL", "KE",
    "KG", "KH", "LK", "LR", "LU", "LY", "MA", "MC", "MG", "MM", "MQ",
    "MW", "MY", "MZ", "NA", "NL", "NZ", "PE", "PL", "PS", "PT", "PY",
    "RE", "RO", "RS", "RW", "SA", "SD", "SE", "SK", "SN", "SO", "SS",
    "SY", "TH", "TJ", "TW", "TZ", "UA", "UG", "UZ", "VN", "XA", "YE",
    "YT", "YU", "ZA", "ZM", "ZW"
  ],
  10: [
    "AR", "AT", "BD", "BJ", "CA", "CI", "CO", "DE", "DO", "EG", "GB",
    "GR", "ID", "IN", "IQ", "IR", "IT", "JM", "JP", "KP", "KR", "KZ",
    "LA", "MX", "NG", "NP", "PH", "PK", "PR", "RU", "SM", "TR", "UM",
    "US", "VA", "VE", "VI", "XG", "XJ", "XM", "XU"
  ],
  11: ["BR", "CN"],
});

// Build lookup object automatically
const COUNTRY_PHONE_LENGTH_BY_SORTNAME = Object.freeze(
  Object.entries(PHONE_LENGTH_GROUPS).reduce((map, [length, countries]) => {
    countries.forEach((country) => {
      map[country] = Number(length);
    });
    return map;
  }, {})
);

/** Common dial-code fallbacks when country sortname is unavailable */
const PHONE_LENGTH_BY_DIAL_CODE = Object.freeze({
  1: 10,
  7: 10,
  20: 10,
  27: 9,
  30: 10,
  31: 9,
  32: 9,
  33: 9,
  34: 9,
  36: 9,
  39: 10,
  40: 9,
  41: 9,
  43: 10,
  44: 10,
  45: 8,
  46: 9,
  47: 8,
  48: 9,
  49: 10,
  51: 9,
  52: 10,
  53: 8,
  54: 10,
  55: 11,
  56: 9,
  57: 10,
  58: 10,
  60: 9,
  61: 9,
  62: 10,
  63: 10,
  64: 9,
  65: 8,
  66: 9,
  81: 10,
  82: 10,
  84: 9,
  86: 11,
  90: 10,
  91: 10,
  92: 10,
  93: 9,
  94: 9,
  95: 9,
  98: 10,
  212: 9,
  213: 9,
  216: 8,
  218: 9,
  220: 7,
  221: 9,
  229: 10,
  231: 9,
  234: 10,
  245: 9,
  252: 9,
  254: 9,
  255: 9,
  256: 9,
  260: 9,
  263: 9,
  351: 9,
  352: 9,
  353: 9,
  354: 7,
  358: 9,
  359: 9,
  370: 8,
  371: 8,
  372: 8,
  380: 9,
  381: 9,
  385: 9,
  386: 8,
  420: 9,
  421: 9,
  686: 8,
  852: 8,
  853: 8,
  855: 9,
  856: 10,
  880: 10,
  886: 9,
  960: 7,
  961: 8,
  962: 9,
  963: 9,
  964: 10,
  965: 8,
  966: 9,
  967: 9,
  968: 8,
  971: 9,
  972: 9,
  973: 8,
  974: 8,
  975: 8,
  976: 8,
  977: 10,
  992: 9,
  993: 8,
  994: 9,
  995: 9,
  996: 9,
  998: 9,
});

const normalizeDialCode = (phoneCode) =>
  String(phoneCode ?? "").replace(/\D/g, "");

export const getCountryPhoneLength = (country) => {
  if (!country?.sortname) return null;

  return COUNTRY_PHONE_LENGTH_BY_SORTNAME[country.sortname.toUpperCase()] ?? null;
};

export const getPhoneLengthByPhoneCode = (phoneCode, countries = []) => {
  const code = normalizeDialCode(phoneCode);
  if (!code) return null;

  const matches = (Array.isArray(countries) ? countries : []).filter(
    (country) =>
      normalizeDialCode(country?.phoneCode ?? country?.phonecode) === code
  );

  // A dial code shared by several countries (e.g. +1, +7, +44) can't be resolved to one
  // country, so the dial-code table decides when it knows the code.
  if (matches.length > 1 && PHONE_LENGTH_BY_DIAL_CODE[code]) {
    return PHONE_LENGTH_BY_DIAL_CODE[code];
  }

  const match = matches[0];
  if (match) {
    const fromSortname = getCountryPhoneLength({
      sortname: match.sortname || match.iso2 || match.code,
    });
    if (fromSortname) return fromSortname;
  }

  return PHONE_LENGTH_BY_DIAL_CODE[code] ?? null;
};

/**
 * Phone code dropdown options from a countries list: one option per dial code, sorted numerically.
 * sortname is kept only when the code belongs to a single country, so a shared code (+1, +7, +44)
 * falls back to the dial-code table in getPhoneLengthByPhoneCode instead of an arbitrary country.
 */
export const buildPhoneCodeOptions = (countries = []) => {
  const byCode = new Map();
  (Array.isArray(countries) ? countries : []).forEach((country) => {
    const phoneCode = country?.phoneCode ?? country?.phonecode;
    const code = normalizeDialCode(phoneCode);
    if (!code) return;
    const existing = byCode.get(code);
    if (existing) {
      existing.count += 1;
    } else {
      byCode.set(code, { phoneCode, sortname: country?.sortname, count: 1 });
    }
  });
  return [...byCode.entries()]
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, { phoneCode, sortname, count }]) => ({
      label: phoneCode,
      value: phoneCode,
      phoneCode,
      ...(count === 1 && sortname ? { sortname } : {}),
    }));
};

export const sanitizePhoneDigits = (value = "", maxLength) => {
  const digits = String(value).replace(/\D/g, "");
  return maxLength ? digits.slice(0, maxLength) : digits;
};

export const getPhoneValidationError = (number, country, phoneCode, countries) => {
  const requiredLength =
    getCountryPhoneLength(country) ||
    getPhoneLengthByPhoneCode(phoneCode, countries);

  if (!requiredLength) return null;

  const digits = sanitizePhoneDigits(number);
  if (!digits) return null;

  return digits.length === requiredLength
    ? null
    : `Enter a valid ${requiredLength}-digit number`;
};
